import axios from "axios";
import LocalStorageService from "app/service/localStorageService";

import { isValidAppsCode, sanitizeAppsCode } from "app/utils/appsCode";
import {
  extractTenantSlugFromHostname,
  readTenantContext } from
"app/utils/tenantHost";
import {
  createAuthSessionController,
  isAuthTerminalRequest,
  shouldAttemptTokenRefresh } from
"app/utils/axiosAuthSession";

// Create instance
const axiosInstance = axios.create({
  baseURL: window._env_?.REACT_APP_API_ENDPOINT || "http://localhost:9090/",
  withCredentials: true
});

const localStorageService = LocalStorageService.getService();

const APPS_CODE_STORAGE_KEY = "appsCode";

/** Prefer hostname-resolved tenant appsCode over stale localStorage. */
function resolveAppsCode() {
  const tenant = readTenantContext();
  const fromTenant = sanitizeAppsCode(tenant?.appsCode);
  if (fromTenant) return fromTenant;
  return sanitizeAppsCode(localStorageService.getItem(APPS_CODE_STORAGE_KEY));
}

function resolveTenantSlug() {
  const fromHost = extractTenantSlugFromHostname();
  if (fromHost) return fromHost;
  const tenant = readTenantContext();
  return tenant?.tenantSlug || null;
}

function extractAppsCodeFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  const match = /(?:^|[?&])appsCode=([^&]*)/.exec(url);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function extractAppsCodeFromParams(params) {
  if (params == null) return null;
  if (typeof URLSearchParams !== "undefined" && params instanceof URLSearchParams) {
    return params.get("appsCode");
  }
  if (typeof params === "object" && Object.prototype.hasOwnProperty.call(params, "appsCode")) {
    return params.appsCode;
  }
  return null;
}

function replaceAppsCodeInUrl(url, appsCode) {
  if (!url || typeof url !== "string") return url;
  if (/(?:^|[?&])appsCode=/.test(url)) {
    return url.replace(
      /([?&]appsCode=)[^&]*/,
      `$1${encodeURIComponent(appsCode)}`
    );
  }
  return url;
}

/**
 * Skip injection only when a *valid* appsCode is already present.
 * Placeholder values like appsCode=undefined must be overwritten.
 */
function shouldSkipAppsCodeInjection(config) {
  const url = config.url || "";
  if (url.startsWith("http://") || url.startsWith("https://")) return true;
  if (url.includes("/oauth/token")) return true;
  if (url.includes("refreshToken")) return true;
  if (isValidAppsCode(extractAppsCodeFromParams(config.params))) return true;
  if (isValidAppsCode(extractAppsCodeFromUrl(url))) return true;
  return false;
}

function attachAppsCode(config, appsCode) {
  config.headers["X-Apps-Code"] = appsCode;
  const slug = resolveTenantSlug();
  if (slug) {
    config.headers["X-Tenant-Slug"] = slug;
  }

  if (config.url && /(?:^|[?&])appsCode=/.test(config.url)) {
    config.url = replaceAppsCodeInUrl(config.url, appsCode);
  }

  if (config.params instanceof URLSearchParams) {
    config.params.set("appsCode", appsCode);
    return;
  }

  config.params = {
    ...(config.params || {}),
    appsCode
  };
}

// --------------------
// REQUEST INTERCEPTOR
// --------------------
axiosInstance.interceptors.request.use(
  (config) => {
    config.withCredentials = true;

    // Set default content type
    if (!(config.data instanceof FormData) && !config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    // Handle 2FA token logic
    const isIn2FA = localStorage.getItem("authPhase") === "2FA";
    const token = isIn2FA ?
    localStorage.getItem("tempToken") :
    localStorageService.getAccessToken();

    // Attach token only for API base URL
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    const appsCode = resolveAppsCode();
    if (appsCode && !shouldSkipAppsCodeInjection(config)) {
      attachAppsCode(config, appsCode);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const authSession = createAuthSessionController({
  axiosInstance,
  localStorageService
});

let responseInterceptorId = null;

// --------------------
// RESPONSE INTERCEPTOR
// --------------------
export const setupAxiosInterceptors = (navigate, openSnackbar) => {
  if (responseInterceptorId != null) {
    axiosInstance.interceptors.response.eject(responseInterceptorId);
  }

  responseInterceptorId = axiosInstance.interceptors.response.use(
    (response) => response,
    authSession.createResponseErrorHandler(navigate, openSnackbar)
  );

  return responseInterceptorId;
};

/** Test helper: reset in-flight auth state between cases. */
export function __resetAxiosAuthStateForTests() {
  authSession.resetForTests();
  if (responseInterceptorId != null) {
    axiosInstance.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
}

export { isAuthTerminalRequest, shouldAttemptTokenRefresh };

export default axiosInstance;